import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import type { CotizacionPdfPrecargaResult } from '../../services/cotizacionVentaService';
import {
  debeSugerirCrearCliente,
  labelClientePrecarga,
  MSG_CAMPO_NO_DETECTADO,
  precargaCotizacionUtilizoIa,
  tieneDatosUtilesPrecarga,
} from '../../utils/cotizacionPdfPrecarga';
import { formatCurrency } from '../../utils/formatters';

const MS_ANTES_MENSAJE_IA = 1500;
const AVISO_COSTOS_IA =
  'El uso de inteligencia artificial para complementar datos puede generar costos adicionales según el proveedor del servicio.';

interface PrecargaCotizacionPdfDialogProps {
  open: boolean;
  loading?: boolean;
  precarga: CotizacionPdfPrecargaResult | null;
  onClose: () => void;
  onApply: () => void;
  onCrearCliente?: () => void;
}

const PrecargaCotizacionPdfDialog: React.FC<PrecargaCotizacionPdfDialogProps> = ({
  open,
  loading = false,
  precarga,
  onClose,
  onApply,
  onCrearCliente,
}) => {
  const [faseIa, setFaseIa] = useState(false);

  useEffect(() => {
    if (!loading) {
      setFaseIa(false);
      return undefined;
    }
    setFaseIa(false);
    const timerId = window.setTimeout(() => setFaseIa(true), MS_ANTES_MENSAJE_IA);
    return () => window.clearTimeout(timerId);
  }, [loading]);

  const advertencias = precarga?.advertencias ?? [];
  const items = precarga?.items ?? [];
  const totalPdf = precarga?.formulario?.totalConIva ?? null;
  const sumaItems = items.reduce((acc, item) => {
    const linea = Number(item.importeTotalConIva ?? item.precioUnitario * item.cantidad);
    return acc + (Number.isFinite(linea) ? linea : 0);
  }, 0);
  const totalesCoinciden =
    totalPdf != null && Math.abs(Math.round(sumaItems * 100) - Math.round(Number(totalPdf) * 100)) <= 1;
  const puedeAplicar = tieneDatosUtilesPrecarga(precarga);
  const hayGenericos = items.some((item) => item.usoArticuloGenerico);
  const utilizoIa = precargaCotizacionUtilizoIa(precarga);
  const sugerirCrear = debeSugerirCrearCliente(precarga);
  const clienteOk = Boolean(precarga?.cliente.encontrado);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Precarga presupuesto desde PDF</DialogTitle>
      <DialogContent>
        {loading ? (
          <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ py: 2 }}>
            <CircularProgress size={20} sx={{ mt: 0.25, flexShrink: 0 }} />
            <Stack spacing={0.5}>
              <Typography variant="body2" color="text.secondary">
                {faseIa ? 'Complementando con inteligencia artificial…' : 'Analizando el PDF…'}
              </Typography>
              {faseIa ? (
                <Typography variant="caption" color="text.secondary">
                  {AVISO_COSTOS_IA}
                </Typography>
              ) : null}
            </Stack>
          </Stack>
        ) : precarga ? (
          <Stack spacing={2}>
            {clienteOk && (
              <Alert severity="success" variant="outlined">
                Cliente: <strong>{labelClientePrecarga(precarga)}</strong>
              </Alert>
            )}

            {sugerirCrear && (
              <Alert
                severity="info"
                variant="outlined"
                action={
                  onCrearCliente ? (
                    <Button color="inherit" size="small" onClick={onCrearCliente}>
                      Crear cliente
                    </Button>
                  ) : undefined
                }
              >
                El cliente no está registrado: <strong>{labelClientePrecarga(precarga)}</strong>.
                Podés darlo de alta o se creará al importar desde carpeta.
              </Alert>
            )}

            {utilizoIa && (
              <Alert severity="info" variant="outlined">
                Parte de los datos fueron interpretados con inteligencia artificial. {AVISO_COSTOS_IA}
              </Alert>
            )}

            {advertencias.length > 0 && (
              <Alert severity="warning" variant="outlined">
                <Stack spacing={0.5}>
                  {advertencias.map((a) => (
                    <Typography key={a} variant="body2">
                      {a}
                    </Typography>
                  ))}
                </Stack>
              </Alert>
            )}

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Encabezado
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Campo</TableCell>
                      <TableCell>Valor</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      { label: 'Nro presupuesto', value: precarga.formulario?.ordenDeCompra },
                      { label: 'Fecha', value: precarga.formulario?.fechaEmision },
                      { label: 'Forma de pago', value: precarga.formulario?.formaDePago },
                      { label: 'Vendedor PDF', value: precarga.formulario?.vendedorPdf },
                      {
                        label: 'Total con IVA',
                        value: precarga.formulario?.totalConIva != null
                          ? formatCurrency(precarga.formulario.totalConIva)
                          : null,
                      },
                      { label: 'Observaciones', value: precarga.formulario?.descripcion },
                    ].map((row) => {
                      const noDetectado = !row.value;
                      return (
                        <TableRow key={row.label}>
                          <TableCell>{row.label}</TableCell>
                          <TableCell>
                            {noDetectado ? (
                              <Stack direction="row" spacing={0.5} alignItems="flex-start">
                                <WarningAmberOutlinedIcon color="warning" sx={{ fontSize: 16, mt: 0.15 }} />
                                <Typography variant="body2" color="warning.main">
                                  {MSG_CAMPO_NO_DETECTADO}
                                </Typography>
                              </Stack>
                            ) : (
                              row.value
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {totalPdf != null && items.length > 0 && (
              <Alert severity={totalesCoinciden ? 'success' : 'error'} variant="outlined">
                {totalesCoinciden
                  ? `El total de los ítems coincide con el total del PDF (${formatCurrency(totalPdf)}).`
                  : `El total del PDF (${formatCurrency(totalPdf)}) no coincide con la suma de los ítems (${formatCurrency(sumaItems)}).`}
              </Alert>
            )}

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Ítems ({items.length})
              </Typography>
              <TableContainer sx={{ maxHeight: 280 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Código</TableCell>
                      <TableCell>Descripción</TableCell>
                      <TableCell align="right">Cant.</TableCell>
                      <TableCell align="right">P. unit. neto</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Typography variant="body2" color="text.secondary">
                            Sin líneas detectadas.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      items.map((item, idx) => (
                        <TableRow key={`${item.codigoArticulo}-${idx}`}>
                          <TableCell>
                            {item.codigoArticulo || '—'}
                            {item.usoArticuloGenerico ? (
                              <Typography variant="caption" color="warning.main" display="block">
                                Genérico
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {item.descripcionOriginal || item.descripcion || '—'}
                            </Typography>
                            {item.usoArticuloGenerico && item.renglonPdf ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', whiteSpace: 'pre-wrap' }}
                              >
                                {`PDF: ${item.renglonPdf}`}
                              </Typography>
                            ) : null}
                            {item.advertencia ? (
                              <Typography variant="caption" color="warning.main" display="block">
                                {item.advertencia}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell align="right">{item.cantidad}</TableCell>
                          <TableCell align="right">{formatCurrency(item.precioUnitario)}</TableCell>
                          <TableCell align="right">
                            {formatCurrency(item.importeTotalConIva ?? item.precioUnitario * item.cantidad)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Typography variant="caption" color="text.secondary">
              Revise los datos antes de aplicar. La cotización no se guarda hasta que confirme en el POS.
            </Typography>
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={onApply} disabled={loading || !puedeAplicar}>
          {hayGenericos ? 'Corregir artículos' : 'Aplicar precarga'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrecargaCotizacionPdfDialog;
